import React, { useState, useEffect } from 'react'
import { Divider } from 'components/divider'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, BellAlertIcon } from '@heroicons/react/20/solid'
import { useNavigate } from 'react-router-dom'
import { useMutation, useLazyQuery } from '@apollo/client'
import { SEND_ADMIN_PUSH_NOTIFICATION } from 'graphql/operations/mutations'
import { GET_CAPTAIN_ROUTE_BOAT_HISTORY_LIST } from 'graphql/operations/queries'
import moment from 'moment'
import Flatpickr from 'react-flatpickr'

export default function SendNotification() {
    const { t } = useTranslation()
    const navigate = useNavigate()

    // Form state
    const [sendTo, setSendTo] = useState('ALL_USERS')
    const [titleEn, setTitleEn] = useState('')
    const [titleAr, setTitleAr] = useState('')
    const [descriptionEn, setDescriptionEn] = useState('')
    const [descriptionAr, setDescriptionAr] = useState('')

    // Trip Users specific state
    const [routeType, setRouteType] = useState('DMC') // DMC for Domestic, INL for International
    const [tripDate, setTripDate] = useState(moment().format('YYYY-MM-DD'))
    const [selectedTrip, setSelectedTrip] = useState('')
    const [tripList, setTripList] = useState([])

    // Dialog state
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    // Validation errors
    const [errors, setErrors] = useState({})

    // Get trip list from Route Operations API
    const [getTripList, { loading: tripListLoading }] = useLazyQuery(
        GET_CAPTAIN_ROUTE_BOAT_HISTORY_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getCaptainRouteBoatHistory.success && data.getCaptainRouteBoatHistory.code === '200') {
                    // Filter trips by route type
                    const filteredTrips = data.getCaptainRouteBoatHistory.history.filter(
                        history => history.route?.master_route?.route_type?.code === routeType
                    )
                    setTripList(filteredTrips)
                    setSelectedTrip('') // Reset selected trip when list changes
                }
            },
            onError: error => {
                setShowErrDialog(true)
                setErrorMessage(error.message || t('Failed to fetch trips'))
            }
        }
    )

    // Fetch trips when route type or date changes
    useEffect(() => {
        if (sendTo === 'TRIP_USERS' && tripDate) {
            getTripList({
                variables: {
                    captainId: null,
                    routeId: null,
                    boatId: null,
                    historyId: null,
                    activityDate: tripDate,
                },
            })
        }
    }, [sendTo, routeType, tripDate])

    // Send notification mutation
    const [sendNotification, { loading: sendingNotification }] = useMutation(SEND_ADMIN_PUSH_NOTIFICATION, {
        onCompleted: data => {
            if (data?.sendAdminPushNotification?.success && data?.sendAdminPushNotification?.code === '200') {
                setShowSucDialog(true)
                setSuccessMessage(data?.sendAdminPushNotification?.message || t('Notification sent successfully'))
                // Reset form
                resetForm()
            } else {
                setShowErrDialog(true)
                setErrorMessage(data?.sendAdminPushNotification?.message || t('Failed to send notification'))
            }
        },
        onError: error => {
            setShowErrDialog(true)
            setErrorMessage(error.message || t('An error occurred while sending notification'))
        },
    })

    const resetForm = () => {
        setTitleEn('')
        setTitleAr('')
        setDescriptionEn('')
        setDescriptionAr('')
        setSelectedTrip('')
        setErrors({})
    }

    const validateForm = () => {
        const newErrors = {}

        if (!titleEn.trim()) {
            newErrors.titleEn = t('Title (English) is required')
        }
        if (!titleAr.trim()) {
            newErrors.titleAr = t('Title (Arabic) is required')
        }
        if (!descriptionEn.trim()) {
            newErrors.descriptionEn = t('Description (English) is required')
        }
        if (!descriptionAr.trim()) {
            newErrors.descriptionAr = t('Description (Arabic) is required')
        }

        if (sendTo === 'TRIP_USERS') {
            if (!tripDate) {
                newErrors.tripDate = t('Trip date is required')
            }
            if (!selectedTrip) {
                newErrors.selectedTrip = t('Please select a trip')
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = () => {
        if (!validateForm()) {
            return
        }

        const notificationPayload = {
            send_to: sendTo,
            message: {
                content: {
                    en: descriptionEn.trim(),
                    ar: descriptionAr.trim(),
                },
                heading: {
                    en: titleEn.trim(),
                    ar: titleAr.trim(),
                },
            },
        }

        // Add trips array for TRIP_USERS
        if (sendTo === 'TRIP_USERS' && selectedTrip) {
            const selectedTripData = tripList.find(trip => trip.history_id === selectedTrip)
            if (selectedTripData) {
                notificationPayload.trips = [
                    {
                        trip_date: tripDate,
                        trip_id: selectedTripData.trip?.trip_id,
                    },
                ]
            }
        }

        sendNotification({
            variables: {
                notification: notificationPayload,
            },
        })
    }

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }

    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }

    // Format trip time for display
    const formatTripTime = (startTime, endTime) => {
        if (!startTime) return '-'
        const start = moment(startTime).format('HH:mm')
        const end = endTime ? moment(endTime).format('HH:mm') : ''
        return end ? `${start} - ${end}` : start
    }

    return (
        <>
            {/* Header */}
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center sm:flex-nowrap">
                        <div className="mx-auto flex flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                            <BellAlertIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3 text-base font-semibold">{t('Send Notification')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Send push notifications to all users or users of a specific trip')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <Divider />

            {/* Form */}
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="space-y-6">
                    {/* Send To Dropdown - Limited width */}
                    <div className="max-w-xs">
                        <label htmlFor="sendTo" className="block text-sm font-medium leading-6 text-gray-900">
                            {t('Send To')} <span className="text-red">*</span>
                        </label>
                        <div className="mt-2">
                            <select
                                id="sendTo"
                                name="sendTo"
                                value={sendTo}
                                onChange={e => {
                                    setSendTo(e.target.value)
                                    setSelectedTrip('')
                                    setErrors({})
                                }}
                                className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                            >
                                <option value="ALL_USERS">{t('All Users')}</option>
                                <option value="TRIP_USERS">{t('Trip Users')}</option>
                            </select>
                        </div>
                    </div>

                    {/* Trip Users specific fields */}
                    {sendTo === 'TRIP_USERS' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Route Type */}
                            <div>
                                <label htmlFor="routeType" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Route Type')} <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <select
                                        id="routeType"
                                        name="routeType"
                                        value={routeType}
                                        onChange={e => {
                                            setRouteType(e.target.value)
                                            setSelectedTrip('')
                                        }}
                                        className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    >
                                        <option value="DMC">{t('Domestic')}</option>
                                        <option value="INL">{t('International')}</option>
                                    </select>
                                </div>
                            </div>

                            {/* Trip Date */}
                            <div>
                                <label htmlFor="tripDate" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Trip Date')} <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <Flatpickr
                                        id="tripDate"
                                        name="tripDate"
                                        options={{
                                            dateFormat: 'Y-m-d',
                                            minDate: 'today',
                                        }}
                                        value={tripDate}
                                        onChange={date => {
                                            if (date[0]) {
                                                setTripDate(moment(date[0]).format('YYYY-MM-DD'))
                                                setSelectedTrip('')
                                            }
                                        }}
                                        className={`block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ${
                                            errors.tripDate ? 'ring-red' : 'ring-gray-300'
                                        } focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6`}
                                    />
                                </div>
                                {errors.tripDate && (
                                    <p className="mt-1 text-sm text-red">{errors.tripDate}</p>
                                )}
                            </div>

                            {/* Trip List */}
                            <div>
                                <label htmlFor="tripList" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Select Trip')} <span className="text-red">*</span>
                                </label>
                                <div className="mt-2">
                                    <select
                                        id="tripList"
                                        name="tripList"
                                        value={selectedTrip}
                                        onChange={e => setSelectedTrip(e.target.value)}
                                        disabled={tripListLoading}
                                        className={`block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ${
                                            errors.selectedTrip ? 'ring-red' : 'ring-gray-300'
                                        } focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                    >
                                        {tripListLoading ? (
                                            <option value="">{t('Loading...')}</option>
                                        ) : tripList.length === 0 ? (
                                            <option value="">{t('No trips found')}</option>
                                        ) : (
                                            <>
                                                <option value="">{t('Select a trip')}</option>
                                                {tripList.map(trip => (
                                                    <option key={trip.history_id} value={trip.history_id}>
                                                        {trip.route?.route_name?.en || '-'} - {trip.trip?.trip_name?.en || '-'} ({formatTripTime(trip.start_time, trip.end_time)}){trip.is_trip_private ? ' [Private]' : ''}
                                                    </option>
                                                ))}
                                            </>
                                        )}
                                    </select>
                                </div>
                                {errors.selectedTrip && (
                                    <p className="mt-1 text-sm text-red">{errors.selectedTrip}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Title Row - 2 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Title English */}
                        <div>
                            <label htmlFor="titleEn" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Title (English)')} <span className="text-red">*</span>
                            </label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    id="titleEn"
                                    name="titleEn"
                                    value={titleEn}
                                    onChange={e => setTitleEn(e.target.value)}
                                    placeholder={t('Enter notification title in English')}
                                    className={`block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ${
                                        errors.titleEn ? 'ring-red' : 'ring-gray-300'
                                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6`}
                                />
                            </div>
                            {errors.titleEn && (
                                <p className="mt-1 text-sm text-red">{errors.titleEn}</p>
                            )}
                        </div>

                        {/* Title Arabic */}
                        <div>
                            <label htmlFor="titleAr" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Title (Arabic)')} <span className="text-red">*</span>
                            </label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    id="titleAr"
                                    name="titleAr"
                                    value={titleAr}
                                    onChange={e => setTitleAr(e.target.value)}
                                    placeholder={t('Enter notification title in Arabic')}
                                    dir="rtl"
                                    className={`block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ${
                                        errors.titleAr ? 'ring-red' : 'ring-gray-300'
                                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6`}
                                />
                            </div>
                            {errors.titleAr && (
                                <p className="mt-1 text-sm text-red">{errors.titleAr}</p>
                            )}
                        </div>
                    </div>

                    {/* Description Row - 2 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Description English */}
                        <div>
                            <label htmlFor="descriptionEn" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Description (English)')} <span className="text-red">*</span>
                            </label>
                            <div className="mt-2">
                                <textarea
                                    id="descriptionEn"
                                    name="descriptionEn"
                                    rows={4}
                                    value={descriptionEn}
                                    onChange={e => setDescriptionEn(e.target.value)}
                                    placeholder={t('Enter notification description in English')}
                                    className={`block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ${
                                        errors.descriptionEn ? 'ring-red' : 'ring-gray-300'
                                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6`}
                                />
                            </div>
                            {errors.descriptionEn && (
                                <p className="mt-1 text-sm text-red">{errors.descriptionEn}</p>
                            )}
                        </div>

                        {/* Description Arabic */}
                        <div>
                            <label htmlFor="descriptionAr" className="block text-sm font-medium leading-6 text-gray-900">
                                {t('Description (Arabic)')} <span className="text-red">*</span>
                            </label>
                            <div className="mt-2">
                                <textarea
                                    id="descriptionAr"
                                    name="descriptionAr"
                                    rows={4}
                                    value={descriptionAr}
                                    onChange={e => setDescriptionAr(e.target.value)}
                                    placeholder={t('Enter notification description in Arabic')}
                                    dir="rtl"
                                    className={`block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ${
                                        errors.descriptionAr ? 'ring-red' : 'ring-gray-300'
                                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6`}
                                />
                            </div>
                            {errors.descriptionAr && (
                                <p className="mt-1 text-sm text-red">{errors.descriptionAr}</p>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                            {t('Clear')}
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={sendingNotification}
                            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {sendingNotification ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t('Sending...')}
                                </span>
                            ) : (
                                t('Send Notification')
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Dialogs */}
            {showErrDialog && (
                <ErrorDialog status={showErrDialog} onClose={handleErrClose} title={t('Error')} message={errorMessage} />
            )}
            {showSucDialog && (
                <SuccessDialog status={showSucDialog} onClose={handleSucClose} title={t('Success')} message={successMessage} />
            )}
        </>
    )
}
